import {
  forwardRef,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ElementType,
  type KeyboardEvent,
  type MouseEvent,
  type MutableRefObject,
  type ReactNode,
  type Ref,
  type RefAttributes,
} from 'react';
import classNames from 'classnames';
import { Icon, IconName } from '@components/icon';
import './NavButton.scss';

/** Anchor attributes forwarded to the link root, excluding `href` and `children`. */
export type NavButtonLinkRootSharedProps = Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'children' | 'href'
>;

/** Props for native `<a>`; `href` may be omitted for disabled action links. */
export type NavButtonNativeAnchorProps = NavButtonLinkRootSharedProps & {
  href?: string;
};

/** Props forwarded to custom link roots; `href` is always provided. */
export type NavButtonForwardedLinkProps = NavButtonLinkRootSharedProps & {
  href: string;
  children: ReactNode;
};

/** Props for a custom `linkComponent`, including the forwarded `ref`. */
export type NavButtonLinkComponentProps = NavButtonForwardedLinkProps &
  RefAttributes<HTMLAnchorElement>;

interface NavButtonSharedProps {
  children: ReactNode;
  disabled?: boolean;
  size?: 'small' | 'large';
  startIcon?: IconName;
  variant?: 'action' | 'nav';
  className?: string;
  /** Custom link root (e.g. Next.js `Link`). Must accept {@link NavButtonLinkComponentProps}. */
  linkComponent?: ElementType<NavButtonLinkComponentProps>;
}

type NavButtonAsButtonProps = NavButtonSharedProps &
  Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    keyof NavButtonSharedProps | 'children' | 'type'
  > & {
    href?: undefined;
    onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  };

type NavButtonAsAnchorProps = NavButtonSharedProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof NavButtonSharedProps | 'children'> & {
    href: string;
    onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
    type?: never;
  };

export type NavButtonProps = NavButtonAsButtonProps | NavButtonAsAnchorProps;

const isLinkMode = (href: NavButtonProps['href']): href is string => typeof href === 'string';

const BLANK_TARGET_REL_TOKENS = ['noopener', 'noreferrer'] as const;

/** Merge reverse-tabnabbing tokens into any custom `rel` for `target="_blank"`. */
const resolveAnchorRel = (
  target: AnchorHTMLAttributes<HTMLAnchorElement>['target'],
  rel: AnchorHTMLAttributes<HTMLAnchorElement>['rel'],
): string | undefined => {
  if (target?.toLowerCase() !== '_blank') {
    return rel;
  }

  const tokens = new Set(
    (rel ?? '').split(/\s+/).filter(token => token && token.toLowerCase() !== 'opener'),
  );
  BLANK_TARGET_REL_TOKENS.forEach(token => tokens.add(token));
  return [...tokens].join(' ');
};

const REACT_FORWARD_REF_TYPE = Symbol.for('react.forward_ref');
const REACT_MEMO_TYPE = Symbol.for('react.memo');

type RefCapableComponent = ElementType & {
  $$typeof?: symbol;
  type?: RefCapableComponent;
};

/** Native elements and forwardRef (including memo-wrapped) link roots can receive refs. */
const canLinkComponentReceiveRef = (component: ElementType): boolean => {
  if (typeof component === 'string') {
    return true;
  }

  const typed = component as RefCapableComponent;

  if (typed.$$typeof === REACT_FORWARD_REF_TYPE) {
    return true;
  }

  if (typed.$$typeof === REACT_MEMO_TYPE && typed.type) {
    return canLinkComponentReceiveRef(typed.type);
  }

  return false;
};

/**
 * NavButton component. Renders a native `<button>` by default; pass `href` to render a visually identical anchor.
 * @param children - Button label content.
 * @param disabled - Disables interaction for the `action` variant. Default: `false`.
 * @param href - When set (including an empty string), renders an anchor instead of a button. Use a non-empty URL for navigation.
 * @param linkComponent - Custom link root (e.g. Next.js `Link`). Must accept {@link NavButtonLinkComponentProps}. Defaults to `a`.
 * @param onClick - Click handler.
 * @param size - Button size. Default: `large`.
 * @param startIcon - Optional leading icon name.
 * @param variant - Visual variant. Default: `nav` (solid primary).
 * @param className - Additional class name.
 */
export const NavButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, NavButtonProps>(
  (
    {
      children,
      disabled,
      startIcon,
      onClick,
      size = 'large',
      variant = 'nav',
      className,
      href,
      linkComponent,
      ...rest
    },
    ref,
  ) => {
    const isAction = variant === 'action';
    const isInteractionDisabled = isAction && disabled;
    const navButtonClassName = classNames(
      'vds-nav-button',
      `vds-nav-button--${size}`,
      `vds-nav-button--${isAction ? 'outlined' : 'solid'}`,
      { 'vds-nav-button--disabled': isInteractionDisabled },
      className,
    );

    const content = (
      <>
        {startIcon ? (
          <span className="vds-nav-button__icon vds-nav-button__icon--start">
            <Icon name={startIcon} size="small" />
          </span>
        ) : null}

        <span className={classNames('vds-nav-button__text', `vds-button-${variant}-text-${size}`)}>
          {children}
        </span>
      </>
    );

    if (isLinkMode(href)) {
      const LinkWrapper = linkComponent || 'a';
      const {
        tabIndex,
        onKeyDown,
        onAuxClick,
        'aria-disabled': ariaDisabled,
        ...anchorRest
      } = rest as Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof NavButtonSharedProps>;

      const handleAnchorClick = (event: MouseEvent<HTMLAnchorElement>) => {
        if (isInteractionDisabled) {
          event.preventDefault();
          return;
        }

        (onClick as NavButtonAsAnchorProps['onClick'])?.(event);
      };

      const handleAnchorAuxClick = (event: MouseEvent<HTMLAnchorElement>) => {
        if (isInteractionDisabled) {
          event.preventDefault();
          return;
        }

        onAuxClick?.(event);
      };

      const handleAnchorKeyDown = (event: KeyboardEvent<HTMLAnchorElement>) => {
        onKeyDown?.(event);

        if (isInteractionDisabled) {
          if (event.code === 'Enter' || event.code === 'Space') {
            event.preventDefault();
          }
          return;
        }

        if (event.defaultPrevented) return;

        if (event.code === 'Space') {
          event.preventDefault();

          if (!event.repeat) {
            event.currentTarget.click();
          }
        }
      };

      const setAnchorRef = (node: HTMLAnchorElement | null) => {
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as MutableRefObject<HTMLAnchorElement | null>).current = node;
        }
      };

      const sharedLinkProps: NavButtonLinkRootSharedProps = {
        ...anchorRest,
        className: navButtonClassName,
        onClick: handleAnchorClick,
        onAuxClick: handleAnchorAuxClick,
        onKeyDown: handleAnchorKeyDown,
        'aria-disabled': isInteractionDisabled ? true : ariaDisabled,
        tabIndex: isInteractionDisabled ? -1 : tabIndex,
        rel: resolveAnchorRel(anchorRest.target, anchorRest.rel),
      };

      if (LinkWrapper === 'a') {
        const nativeAnchorProps: NavButtonNativeAnchorProps = {
          ...sharedLinkProps,
          ...(isInteractionDisabled ? {} : { href }),
        };

        return (
          <a ref={setAnchorRef} {...nativeAnchorProps}>
            {content}
          </a>
        );
      }

      const linkComponentProps: NavButtonLinkRootSharedProps & { href: string } = {
        ...sharedLinkProps,
        href,
      };

      if (canLinkComponentReceiveRef(LinkWrapper)) {
        return (
          <LinkWrapper ref={setAnchorRef} {...linkComponentProps}>
            {content}
          </LinkWrapper>
        );
      }

      return <LinkWrapper {...linkComponentProps}>{content}</LinkWrapper>;
    }

    return (
      <button
        className={navButtonClassName}
        ref={ref as Ref<HTMLButtonElement>}
        onClick={onClick as NavButtonAsButtonProps['onClick']}
        disabled={isInteractionDisabled}
        {...(rest as Omit<
          ButtonHTMLAttributes<HTMLButtonElement>,
          keyof NavButtonSharedProps | 'type'
        >)}
        type="button">
        {content}
      </button>
    );
  },
);

NavButton.displayName = 'NavButton';
